import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { ArrowRight, Clock, Newspaper, Loader2 } from "lucide-react";

const CATEGORY_STYLES = {
  "cee-technique": { color: "#10B981", bg: "bg-[#10B981]/10", text: "text-[#065F46]", label: "CEE · Technique" },
  "renovation": { color: "#0055A4", bg: "bg-[#0055A4]/10", text: "text-[#9A3412]", label: "Rénovation" },
  "environnement": { color: "#0EA5E9", bg: "bg-[#0EA5E9]/10", text: "text-[#075985]", label: "Environnement" },
  "reglementation": { color: "#8B5CF6", bg: "bg-[#8B5CF6]/10", text: "text-[#5B21B6]", label: "Réglementation" },
};

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function ArticleCard({ article, featured = false }) {
  const cat = CATEGORY_STYLES[article.category] || CATEGORY_STYLES["cee-technique"];
  return (
    <Link
      to={`/blog/${article.slug}`}
      data-testid={`article-card-${article.slug}`}
      className={`group block ${featured ? "lg:col-span-2 lg:row-span-2" : ""}`}
    >
      <article className="h-full bg-white border border-black/[0.06] rounded-2xl overflow-hidden hover:border-[#10B981]/40 hover:shadow-[0_30px_80px_-40px_rgba(15,23,42,0.35)] hover:-translate-y-1 transition-all duration-500">
        <div className={`relative overflow-hidden bg-[#F8FAFC] ${featured ? "aspect-[16/9]" : "aspect-[16/10]"}`}>
          <img
            src={article.image_url}
            alt={article.title}
            loading="lazy"
            className="h-full w-full object-cover group-hover:scale-[1.05] transition-transform duration-[1.2s]"
          />
          <div className="absolute top-4 left-4">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold tracking-[0.14em] uppercase ${cat.bg} ${cat.text} backdrop-blur-md`}>
              {cat.label}
            </span>
          </div>
        </div>
        <div className={`p-6 ${featured ? "md:p-8" : ""}`}>
          <div className="flex items-center gap-3 text-[11px] text-[#94A3B8] mb-3">
            <span>{formatDate(article.created_at)}</span>
            <span className="h-1 w-1 rounded-full bg-[#CBD5E1]" />
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{article.reading_time} min</span>
          </div>
          <h3 className={`font-display font-bold text-[#0F172A] leading-tight tracking-tight group-hover:text-[#064E3B] transition-colors ${featured ? "text-2xl md:text-3xl" : "text-lg"}`}>
            {article.title}
          </h3>
          <p className={`mt-3 text-[#475569] leading-relaxed ${featured ? "text-base line-clamp-3" : "text-sm line-clamp-2"}`}>
            {article.excerpt}
          </p>
          <div className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#10B981] group-hover:gap-2.5 transition-all">
            Lire l&apos;article <ArrowRight className="h-3.5 w-3.5" />
          </div>
        </div>
      </article>
    </Link>
  );
}

export default function Blog() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${API}/blog/categories`)
      .then((r) => r.json())
      .then(setCategories)
      .catch(() => {});
    fetch(`${API}/blog/stats`)
      .then((r) => r.json())
      .then(setStats)
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const url = new URL(`${API}/blog/articles`);
    if (activeCategory) url.searchParams.set("category", activeCategory);
    fetch(url)
      .then((r) => r.json())
      .then((data) => setArticles(Array.isArray(data) ? data : []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  // Auto-poll every 25s while list is empty (bootstrap-friendly for fresh production DB)
  useEffect(() => {
    if (loading || articles.length > 0) return undefined;
    const iv = setInterval(() => {
      const url = new URL(`${API}/blog/articles`);
      if (activeCategory) url.searchParams.set("category", activeCategory);
      fetch(url)
        .then((r) => r.json())
        .then((data) => { if (Array.isArray(data) && data.length > 0) setArticles(data); })
        .catch(() => {});
      // Refresh stats too so the counter updates
      fetch(`${API}/blog/stats`).then((r) => r.json()).then(setStats).catch(() => {});
    }, 25000);
    return () => clearInterval(iv);
  }, [loading, articles.length, activeCategory]);

  const [featured, ...rest] = articles;

  return (
    <div data-testid="blog-page" className="bg-white">
      {/* HERO */}
      <section className="relative bg-gradient-to-b from-[#F8FAFC] to-white pt-32 pb-10 md:pt-40 md:pb-12 border-b border-black/[0.04]">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(16,185,129,0.08) 0%, transparent 45%), radial-gradient(circle at 80% 70%, rgba(249,115,22,0.05) 0%, transparent 50%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-10 w-10 rounded-xl bg-[#064E3B] grid place-items-center text-[#10B981]">
                <Newspaper className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase">
                Actualités ECONATION
              </div>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] tracking-tight leading-[1.02]">
              Actualités de la semaine — CEE, rénovation & environnement.
            </h1>
            <p className="mt-6 text-[#475569] text-lg leading-relaxed max-w-2xl">
              Décryptages techniques, analyses réglementaires et actualités climat par les experts ECONATION.
              Publications régulières, sources officielles (ADEME, PNCEE, Légifrance).
            </p>

            {/* Counter badges */}
            {stats && (
              <div data-testid="blog-stats" className="mt-8 flex flex-wrap gap-3">
                <div className="inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-white border border-black/[0.06] shadow-[0_8px_20px_-12px_rgba(15,23,42,0.15)]">
                  <span className="h-2 w-2 rounded-full bg-[#10B981] animate-pulse" />
                  <span className="font-display text-lg font-bold text-[#0F172A]">{stats.published_this_year}</span>
                  <span className="text-[13px] text-[#64748B]">actualité{stats.published_this_year > 1 ? "s" : ""} publiée{stats.published_this_year > 1 ? "s" : ""} en {stats.year}</span>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#064E3B] text-white">
                  <span className="text-[13px] font-semibold">Nouvelles publications hebdomadaires</span>
                </div>
              </div>
            )}
          </div>

          {/* Category filters */}
          <div className="mt-10 flex flex-wrap gap-2">
            <button
              data-testid="filter-all"
              onClick={() => setActiveCategory(null)}
              className={`px-4 py-2 rounded-full text-[13px] font-semibold transition-all ${
                activeCategory === null
                  ? "bg-white text-slate-900"
                  : "bg-white border border-black/[0.08] text-[#475569] hover:border-[#10B981]/50 hover:text-[#0F172A]"
              }`}
            >
              Tous les articles
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                data-testid={`filter-${c.slug}`}
                onClick={() => setActiveCategory(c.slug)}
                className={`px-4 py-2 rounded-full text-[13px] font-semibold transition-all ${
                  activeCategory === c.slug
                    ? "bg-white text-slate-900"
                    : "bg-white border border-black/[0.08] text-[#475569] hover:border-[#10B981]/50 hover:text-[#0F172A]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 md:py-12">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#94A3B8]">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-3 text-sm">Chargement des articles…</span>
          </div>
        ) : articles.length === 0 ? (
          <div data-testid="blog-empty" className="text-center py-24 max-w-2xl mx-auto">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#10B981]/10 text-[#10B981] mb-5">
              <Loader2 className="h-7 w-7 animate-spin" />
            </div>
            <div className="font-display text-2xl md:text-3xl font-bold text-[#0F172A]">
              Publication du premier contenu en cours…
            </div>
            <p className="mt-4 text-[#64748B] leading-relaxed">
              Notre IA rédactrice prépare vos premiers articles historiques. Les premières publications apparaîtront ici dans <strong className="text-[#0F172A]">quelques minutes</strong>.
              Revenez très bientôt.
            </p>
            <button
              onClick={() => window.location.reload()}
              data-testid="blog-refresh-btn"
              className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white hover:bg-[#064E3B] text-slate-900 text-sm font-semibold transition-colors"
            >
              <Loader2 className="h-4 w-4" /> Actualiser la page
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 auto-rows-fr gap-6 md:gap-8">
            {featured && <ArticleCard article={featured} featured />}
            {rest.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
